import { AnonymousCredential, BlockBlobClient } from "@azure/storage-blob";
import { SetupInstance } from "../../app/msal-account-helpers";
import { protectedResources } from "../../authConfig";
import { InteractionRequiredAuthError, InteractionStatus } from '@azure/msal-browser';
import { useLocal } from './../environment';
import polly from 'polly-js';
import { AccessTokenHelper } from '../access-token-helper';
import { msalConfig } from '../../app';

export class AzureBlobAPI {
    constructor(instance, inProgress, accounts, forceRefresh) {

        this.instance = SetupInstance(instance);
        this.inProgress = inProgress;
        this.account = accounts[0];

        this.accessTokenRequest = {
            scopes: [
                ...protectedResources.apiProperty.scopes.read // TODO: this is random... do we need to address this?
            ],
            authority: `https://login.microsoftonline.com/${msalConfig.auth.tenantId}/`,
            forceRefresh: forceRefresh,
            account: this.account,
        };

        this.russetPropertiesFilesApi = useLocal ?
            'http://localhost:8081/api/files/' :
            'https://apim.ianrobertson.co.uk/api/files/v1/';

        this.accessTokenService = new AccessTokenHelper();
    }

    getSASToken = async (object, filePath, permissionsRequested, companyName) => {
        return polly()
            .logger(error => {
                console.error("An error occurred in getSASToken", error);
            })
            .waitAndRetry(5)
            .executeForPromise(async () => {
                if (this.inProgress !== InteractionStatus.None) {
                    return this.instance.acquireTokenSilent(this.accessTokenRequest);
                }

                try {
                    const accessTokenResponse = await this.accessTokenService.getAccessToken(this.instance, this.inProgress, this.accessTokenRequest);
                    
                    if (!accessTokenResponse) {
                        throw new Error("No access token");
                    }

                    const headers = new Headers();
                    headers.append("Authorization", `Bearer ${accessTokenResponse.accessToken}`);

                    const response = await fetch(`${this.russetPropertiesFilesApi}GetSASTokenForUser?` + new URLSearchParams({ blobName: filePath, permissionsRequested: permissionsRequested, companyName: companyName }), {
                        method: "GET",
                        headers: headers
                    });
                    
                    const data = await response.json();
                    return { result: data, object };
                } catch (error) {
                    if (error instanceof InteractionRequiredAuthError) {
                        // Let centralized auth handling manage redirects
                    }
                    console.log(error);
                    throw error;
                }
            });
    };

    uploadFile = async ({ object, result, progressCallback }) => {
        return polly()
            .logger(error => {
                console.error("An error occurred in uploadFile", error);
            })
            .waitAndRetry(5)
            .executeForPromise(async () => {
                try {
                    const blockBlobClient = new BlockBlobClient(result.pathToBlobIncSasToken, new AnonymousCredential());
                    await blockBlobClient.uploadData(object, { onProgress: p => progressCallback(object, object.size - p.loadedBytes) });
                    return { file: object, ok: true, path: result.path };
                } catch (error) {
                    const uploadError = new Error(`Upload failed for file: ${object.name}`);
                    uploadError.file = object;
                    uploadError.ok = false;
                    throw uploadError;
                }
            });
    };

    getFileStructure = (showDeletedItems) => {

        return polly()
            .logger(error => {
                console.error("An error occurred in getFileStructure", error); // will be hit 5 times if issues
            })
            .waitAndRetry(5)
            .executeForPromise(() => {
                return new Promise((resolve, reject) => { // eslint-disable-line no-unused-vars 
                    if (this.inProgress !== InteractionStatus.None) {
                        return this.instance.acquireTokenSilent(this.accessTokenRequest);
                    }

                    this.accessTokenService.getAccessToken(this.instance, this.inProgress, this.accessTokenRequest)
                        .then(accessTokenResponse => {

                            if (!!!accessTokenResponse) {
                                return reject("No access token");
                            }

                            const bearer = `Bearer ${accessTokenResponse.accessToken}`;

                            const headers = new Headers();
                            headers.append("Authorization", bearer);
                            headers.append("Content-Type", "application/json");

                            const options = {
                                method: "GET",
                                headers: headers
                            };

                            const url = new URL(`${this.russetPropertiesFilesApi}GetStorageItems`);
                            url.searchParams.append("showDeletedItems", !!showDeletedItems)

                            fetch(url, options)
                                .then(response => {
                                    if (response.ok) return response.json();
                                    reject(response);
                                })
                                .then(data => {
                                    const result = Object.assign({}, data);
                                    resolve(result);
                                })
                                .catch(error => {
                                    console.log(error);
                                    reject(error)
                                });
                        })
                        .catch((error) => {
                            if (error instanceof InteractionRequiredAuthError) {
                                // Let centralized auth handling manage redirects
                            }
                            console.log(error);
                            reject(error)
                        });
                });
            });
    };

    queueFileDeletion = (fileDeletionModel) => {

        return polly()
            .logger(error => {
                console.error("An error occurred in queueFileDeletion", error); // will be hit 5 times if issues
            })
            .waitAndRetry(5)
            .executeForPromise(() => {
                return new Promise((resolve, reject) => { // eslint-disable-line no-unused-vars 
                    if (this.inProgress !== InteractionStatus.None) {
                        return this.instance.acquireTokenSilent(this.accessTokenRequest);
                    }

                    this.accessTokenService.getAccessToken(this.instance, this.inProgress, this.accessTokenRequest)
                        .then(accessTokenResponse => {

                            if (!!!accessTokenResponse) {
                                return reject("No access token");
                            }

                            const bearer = `Bearer ${accessTokenResponse.accessToken}`;

                            const headers = new Headers();
                            headers.append("Authorization", bearer);
                            headers.append("Content-Type", "application/json");

                            const options = {
                                method: "POST",
                                headers: headers,
                                body: JSON.stringify(fileDeletionModel)
                            };

                            fetch(`${this.russetPropertiesFilesApi}QueueFileDeletion`, options)
                                .then(response => {
                                    if (response.ok) return resolve(true);
                                    return reject(response);
                                })
                                .catch(error => {
                                    console.log(error);
                                    reject(error)
                                });
                        })
                        .catch((error) => {
                            if (error instanceof InteractionRequiredAuthError) {
                                // Let centralized auth handling manage redirects
                            }
                            console.log(error);
                            reject(error)
                        });
                });
            });
    };

    queueFileVersionDeletion = (fileVersionDeletionModel) => {

        return polly()
            .logger(error => {
                console.error("An error occurred in queueFileVersionDeletion", error); // will be hit 5 times if issues
            })
            .waitAndRetry(5)
            .executeForPromise(() => {
                return new Promise((resolve, reject) => { // eslint-disable-line no-unused-vars 
                    if (this.inProgress !== InteractionStatus.None) {
                        return this.instance.acquireTokenSilent(this.accessTokenRequest);
                    }

                    this.accessTokenService.getAccessToken(this.instance, this.inProgress, this.accessTokenRequest)
                        .then(accessTokenResponse => {

                            if (!!!accessTokenResponse) {
                                return reject("No access token");
                            }

                            const bearer = `Bearer ${accessTokenResponse.accessToken}`;

                            const headers = new Headers();
                            headers.append("Authorization", bearer);
                            headers.append("Content-Type", "application/json");

                            const options = {
                                method: "POST",
                                headers: headers,
                                body: JSON.stringify(fileVersionDeletionModel)
                            };

                            fetch(`${this.russetPropertiesFilesApi}QueueFileVersionDeletion`, options)
                                .then(response => {
                                    if (response.ok) return resolve(true);
                                    return reject(response);
                                })
                                .catch(error => {
                                    console.log(error);
                                    reject(error)
                                });
                        })
                        .catch((error) => {
                            if (error instanceof InteractionRequiredAuthError) {
                                // Let centralized auth handling manage redirects
                            }
                            console.log(error);
                            reject(error)
                        });
                });
            });
    };

    promoteToCurrentVersion = (promoteModel) => {

        return polly()
            .logger(error => {
                console.error("An error occurred in promoteToCurrentVersion", error); // will be hit 5 times if issues
            })
            .waitAndRetry(5)
            .executeForPromise(() => {
                return new Promise((resolve, reject) => { // eslint-disable-line no-unused-vars 
                    if (this.inProgress !== InteractionStatus.None) {
                        return this.instance.acquireTokenSilent(this.accessTokenRequest);
                    }

                    this.accessTokenService.getAccessToken(this.instance, this.inProgress, this.accessTokenRequest)
                        .then(accessTokenResponse => {

                            if (!!!accessTokenResponse) {
                                return reject("No access token");
                            }

                            const bearer = `Bearer ${accessTokenResponse.accessToken}`;

                            const headers = new Headers();
                            headers.append("Authorization", bearer);
                            headers.append("Content-Type", "application/json");

                            const options = {
                                method: "PATCH",
                                headers: headers,
                                body: JSON.stringify(promoteModel)
                            };

                            fetch(`${this.russetPropertiesFilesApi}PromotePreviousVersion`, options)
                                .then(response => {
                                    if (response.ok) return resolve(true);
                                    return reject(response);
                                })
                                .catch(error => {
                                    console.log(error);
                                    reject(error)
                                });
                        })
                        .catch((error) => {
                            if (error instanceof InteractionRequiredAuthError) {
                                // Let centralized auth handling manage redirects
                            }
                            console.log(error);
                            reject(error)
                        });
                });
            });
    };
}